import { useState, useEffect } from 'react';
import { Bell, X, Check, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { alertsApi } from '../../api/alerts';
import { UserAlert } from '../../types/alert';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

export function AlertsDropdown() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState<UserAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Get date-fns locale based on current language
  const getDateLocale = () => {
    return i18n.language === 'pt-BR' ? ptBR : enUS;
  };

  useEffect(() => {
    loadUnreadCount();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadAlerts();
    }
  }, [isOpen]);

  const loadUnreadCount = async () => {
    try {
      const count = await alertsApi.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const unreadAlerts = await alertsApi.getUnreadAlerts();
      setAlerts(unreadAlerts);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId: string) => {
    try {
      await alertsApi.markAsRead(alertId);
      setAlerts(alerts.filter(a => a.id !== alertId));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await alertsApi.markAllAsRead();
      setAlerts([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getAlertIcon = (level: string) => {
    switch (level) {
      case 'ERROR':
        return <AlertCircle className="w-5 h-5" style={{ color: '#EF4444' }} />;
      case 'WARNING':
        return <AlertTriangle className="w-5 h-5" style={{ color: '#F59E0B' }} />;
      case 'INFO':
      default:
        return <Info className="w-5 h-5" style={{ color: '#3B82F6' }} />;
    }
  };

  const getAlertColor = (level: string) => {
    switch (level) {
      case 'ERROR':
        return { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C' };
      case 'WARNING':
        return { bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' };
      case 'INFO':
      default:
        return { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' };
    }
  };

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5" style={{ color: 'var(--wl-secondary)' }} />
        {unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 w-4 h-4 text-xs font-bold text-white rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#EF4444' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content */}
          <div
            className="absolute right-0 mt-2 w-96 rounded-lg shadow-lg z-50"
            style={{
              backgroundColor: '#FFF',
              border: '1px solid #E6E6F0',
              maxHeight: '500px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: '#E6E6F0' }}
            >
              <h3 className="text-lg font-semibold" style={{ color: 'var(--wl-secondary)' }}>
                {t('alerts.title')}
              </h3>
              {alerts.length > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#3B82F6' }}
                >
                  {t('alerts.markAllAsRead')}
                </button>
              )}
            </div>

            {/* Alerts List */}
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              {loading ? (
                <div className="p-8 text-center" style={{ color: 'var(--wl-text-muted)' }}>
                  {t('alerts.loading')}
                </div>
              ) : alerts.length === 0 ? (
                <div className="p-8 text-center" style={{ color: 'var(--wl-text-muted)' }}>
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>{t('alerts.noAlerts')}</p>
                </div>
              ) : (
                alerts.map((alert) => {
                  const colors = getAlertColor(alert.level);
                  return (
                    <div
                      key={alert.id}
                      className="p-4 border-b hover:bg-gray-50 transition-colors"
                      style={{ borderColor: '#E6E6F0' }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getAlertIcon(alert.level)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4
                              className="text-sm font-semibold"
                              style={{ color: 'var(--wl-secondary)' }}
                            >
                              {alert.title}
                            </h4>
                            <button
                              onClick={() => handleMarkAsRead(alert.id)}
                              className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                              title={t('alerts.markAsRead')}
                            >
                              <X className="w-4 h-4" style={{ color: 'var(--wl-text-muted)' }} />
                            </button>
                          </div>
                          <p
                            className="text-sm mt-1"
                            style={{ color: 'rgba(50, 71, 92, 0.8)' }}
                          >
                            {alert.message}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span
                              className="text-xs px-2 py-1 rounded"
                              style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.border}`,
                              }}
                            >
                              {t(`alerts.levels.${alert.level}`)}
                            </span>
                            <span
                              className="text-xs"
                              style={{ color: 'var(--wl-text-muted)' }}
                            >
                              {formatDistanceToNow(new Date(alert.createdAt), {
                                addSuffix: true,
                                locale: getDateLocale(),
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
